import React from 'react';
import {Menu, MenuItem, Typography, Chip, Tooltip, Avatar, Grid, Switch} from '@material-ui/core';
import {makeStyles} from '@material-ui/core/styles';
import {
  AccountCircle as AccountCircleIcon,
  VpnKey as VpnKeyIcon,
  ExitToApp as ExitToAppIcon,
  MailOutline as MailOutlineIcon,
  ChromeReaderMode as ChromeReaderModeIcon,
  PictureAsPdf as PictureAsPdfIcon,
  Code as CodeIcon,
} from '@material-ui/icons';
import {Link} from 'react-router-dom';
import ReactGA from 'react-ga4';
import lscache from 'lscache';
import copyToClipboard from 'copy-to-clipboard';
import getTermsAndConditionsFileUrl from './misc/getTermsAndConditionsFileUrl';
import useStore from '../store/store';
import usePersistentAuthStore from '../store/persistentAuthStore';
import usePersistentStore from '../store/persistentStore';
import useDevPanelStore from '../store/devPanelStore';

const useStyles = makeStyles(theme => ({
  hideOnMobile: {
    [theme.breakpoints.down('sm')]: {
      display: 'none',
    },
  },
  managerGridWrapper: {
    margin: theme.spacing(3),
    padding: theme.spacing(1),
    borderRadius: 4,
    background: theme.palette.action.hover,
    width: 'auto',
    cursor: 'pointer',
  },
}));

const ProfileMenu = ({isMobile, isSelfServe}) => {
  const classes = useStyles();
  const profileMenuOpen = useStore(state => state.profileMenuOpen);
  const onboardingSteps = useStore(state => state.onboardingSteps);
  const setState = useStore(state => state.setState);
  const {auth, clearAuth} = usePersistentAuthStore();
  const apiInspectorEnabled = usePersistentStore(state => state.apiInspectorEnabled);
  const setPersistentState = usePersistentStore(state => state.setState);
  const termsAndConditionsFileUrl = getTermsAndConditionsFileUrl(auth);
  const handleProfileMenu = event => {
    setState('profileMenuOpen', profileMenuOpen ? null : event.currentTarget);
  };

  const logout = () => {
    ReactGA.event({
      category: 'Logout',
      action: 'click',
    });
    clearAuth();
  };

  const setApiInspectorEnabled = enabled => {
    setPersistentState('apiInspectorEnabled', enabled);
    if (enabled) {
      useDevPanelStore.getState().setOpen(true);
    } else {
      useDevPanelStore.getState().clearCalls();
      useDevPanelStore.getState().setOpen(false);
    }
  };

  return (
    <>
      <Chip
        onClick={handleProfileMenu}
        style={{border: 'none'}}
        size={'medium'}
        icon={<AccountCircleIcon />}
        label={<Typography className={classes.hideOnMobile}>{auth.username}</Typography>}
        variant="outlined"
      />
      <Menu
        transitionDuration={0}
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: 'right',
        }}
        style={{marginTop: -10}}
        onClose={handleProfileMenu}
        id="profileMenu"
        anchorEl={null}
        anchorReference="none"
        PaperProps={{
          style: {
            padding: 0,
            right: 10,
            top: 70,
          },
        }}
        transformOrigin={{vertical: 'top', horizontal: 'right'}}
        open={profileMenuOpen}
      >
        {auth.manager && auth.hide_manager !== 1 && isMobile && (
          <Tooltip title={`Click to copy email ${auth.manager.email} to clipboard`}>
            <Grid
              direction="column"
              onClick={() => {
                ReactGA.event({
                  category: 'Copy Account Executive email',
                  action: 'click',
                });
                copyToClipboard(auth.manager.email);
              }}
              justify="center"
              alignItems="center"
              spacing={1}
              container
              className={classes.managerGridWrapper}
            >
              <Grid item>
                <Typography color={'textSecondary'}>Your Account Executive</Typography>
              </Grid>
              <Grid item>
                <Avatar
                  style={{height: 20, width: 20}}
                  src={`https://wb.setupad.com/managers/${auth.manager.id}.jpeg`}
                />
              </Grid>
              <Grid item>
                <Typography>{auth.manager.fullName}</Typography>
              </Grid>
              <Grid item>
                <Typography
                  variant={'subtitle'}
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                  }}
                  color={'textSecondary'}
                >
                  <MailOutlineIcon style={{marginRight: 5}} />
                  {auth.manager.email}
                </Typography>
              </Grid>
            </Grid>
          </Tooltip>
        )}

        {auth.manager && auth.hide_manager !== 1 && isMobile && (
          <Tooltip title={`Click to copy email ${auth.manager.email} to clipboard`}>
            <Grid
              direction="column"
              onClick={() => {
                ReactGA.event({
                  category: 'Copy Account Executive email',
                  action: 'click',
                });
                copyToClipboard(auth.manager.email);
              }}
              justify="center"
              alignItems="center"
              spacing={1}
              container
              className={classes.managerGridWrapper}
            >
              <Grid item>
                <Typography color={'textSecondary'}>Your Account Executive</Typography>
              </Grid>
              <Grid item>
                <Avatar
                  style={{height: 20, width: 20}}
                  src={`https://wb.setupad.com/managers/${auth.manager.id}.jpeg`}
                />
              </Grid>
              <Grid item>
                <Typography>{auth.manager.fullName}</Typography>
              </Grid>
              <Grid item>
                <Typography
                  variant={'subtitle'}
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                  }}
                  color={'textSecondary'}
                >
                  <MailOutlineIcon style={{marginRight: 5}} />
                  {auth.manager.email}
                </Typography>
              </Grid>
            </Grid>
          </Tooltip>
        )}
        {auth.has_custom_tos !== 1 && (
          <MenuItem component={'a'} href={termsAndConditionsFileUrl} target={'_blank'}>
            <ChromeReaderModeIcon style={{marginRight: 5}} /> Terms and Conditions
          </MenuItem>
        )}
        {!isSelfServe && (
          <MenuItem
            component={'a'}
            href={'https://client.setupad.com/Setupad_HB_test_2022.pdf'}
            target={'_blank'}
          >
            <PictureAsPdfIcon style={{marginRight: 5}} /> Test Recommendations
          </MenuItem>
        )}
        <MenuItem style={{display: 'flex', alignItems: 'center'}} disableRipple>
          <CodeIcon style={{marginRight: 5}} />
          <span style={{flex: 1}}>API inspector</span>
          <Switch
            color="primary"
            size="small"
            checked={Boolean(apiInspectorEnabled)}
            onChange={(_, checked) => setApiInspectorEnabled(checked)}
          />
        </MenuItem>
        <MenuItem
          component={Link}
          to={'/change-password'}
          selected={onboardingSteps[6]?.active ? true : null}
        >
          <VpnKeyIcon style={{marginRight: 5}} />
          Change password
        </MenuItem>
        <MenuItem onClick={logout}>
          <ExitToAppIcon style={{marginRight: 5}} /> Logout
        </MenuItem>
      </Menu>
    </>
  );
};

export default ProfileMenu;
